import { ErrorCode } from "./Constants";
import { generateUUID } from "./HelperFunctions";

// API fetch timeout (3 seconds)
export const CONFIG_FETCH_TIMEOUT_MS = 3000;

/**
 * Custom error class for fetch operations with error codes
 */
export class FetchError extends Error {
  constructor(message: string, public errorCode: string) {
    super(message);
    this.name = "FetchError";
  }
}

type FetchWithTimeoutResult =
  | { response: Response; error?: never }
  | { response?: never; error: FetchError };

/**
 * Standalone fetch with timeout that returns { response?, error? }
 */
export async function fetchWithTimeout(
  url: string,
  timeoutMs: number = CONFIG_FETCH_TIMEOUT_MS
): Promise<FetchWithTimeoutResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "X-Request-Id": generateUUID(),
      },
      credentials: "include",
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (response.status !== 200) {
      return {
        error: new FetchError(
          `HTTP ${response.status}: ${response.statusText}`,
          getHttpErrorCode(response.status)
        ),
      };
    }

    return { response };
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort/timeout
    if (error instanceof Error && error.name === "AbortError") {
      return {
        error: new FetchError(
          "Request timeout",
          ErrorCode.CONFIG_FETCH_TIMEOUT
        ),
      };
    }

    // Re-use FetchError as-is
    if (error instanceof FetchError) {
      return { error };
    }

    // Unknown error
    return {
      error: new FetchError(
        "Unknown fetch error",
        ErrorCode.CONFIG_FETCH_UNKNOWN_ERROR
      ),
    };
  }
}

/**
 * Maps HTTP status codes to ErrorCode values
 */
export function getHttpErrorCode(status: number): string {
  switch (status) {
    case 400:
      return ErrorCode.CONFIG_FETCH_HTTP_400;
    case 401:
      return ErrorCode.CONFIG_FETCH_HTTP_401;
    case 403:
      return ErrorCode.CONFIG_FETCH_HTTP_403;
    case 404:
      return ErrorCode.CONFIG_FETCH_HTTP_404;
    case 500:
      return ErrorCode.CONFIG_FETCH_HTTP_500;
    case 502:
      return ErrorCode.CONFIG_FETCH_HTTP_502;
    case 503:
      return ErrorCode.CONFIG_FETCH_HTTP_503;
    default:
      return ErrorCode.CONFIG_FETCH_HTTP_OTHER;
  }
}
